import { useMemo, useEffect, useState } from 'react';
import { SubscriptionTier } from '@/../generated-client/models';
import { useDashboardUserAuthId } from '@/stores/map/dashboardStore';
import { getUserAssignedTier } from '@/utils/organization/getUserAssignedTier';

/**
 * @typedef {Object} Group
 * @property {string[]} [connect_user_auth_ids]
 * @property {string[]} [ops_user_auth_ids]
 * @property {string[]} [respond_user_auth_ids]
 */

/**
 * @typedef {Object} Organization
 * @property {number|string} [trial_end_timestamp] // ms epoch or ISO string
 */

/**
 * @typedef {Object} SubscriptionStatus
 * @property {import('@/../generated-client/models').SubscriptionTier} assignedTier
 * @property {import('@/../generated-client/models').SubscriptionTier} effectiveTier
 * @property {boolean} isOnTrial
 * @property {boolean} hasAccess Any paid (Ops or Respond) access, including an active trial.
 * @property {boolean} hasRespondAccess Respond-specific access, including an active trial (trial elevates to Respond).
 */

const DEFAULT_STATUS = Object.freeze({
  assignedTier: SubscriptionTier.Connect,
  effectiveTier: SubscriptionTier.Connect,
  isOnTrial: false,
  hasAccess: false,
  hasRespondAccess: false,
});

/**
 * @param {Group[]} groups
 * @param {string} authId
 * @returns {boolean}
 */
const isUserListedInAnyTierPool = (groups, authId) =>
  groups.some(
    (g) =>
      g?.connect_user_auth_ids?.includes(authId) ||
      g?.ops_user_auth_ids?.includes(authId) ||
      g?.respond_user_auth_ids?.includes(authId),
  );

/**
 * @typedef {object} useSubscriptionStatusProps
 * @property {Array<Group>} groups
 * @property { Organization } organization
 */

/**
 * @param {useSubscriptionStatusProps} props
 * @param {number} [nowMs] - optional current time in milliseconds (ms since epoch). If omitted, trial checks will be conservative (not on trial until caller supplies time).
 * @returns {SubscriptionStatus}
 */
export const useSubscriptionStatus = (groups, organization, nowMs) => {
  const dashboardUserAuthId = useDashboardUserAuthId();
  const now = typeof nowMs === 'number' ? nowMs : null;

  return useMemo(() => {
    if (!dashboardUserAuthId || !Array.isArray(groups) || groups.length === 0) {
      return DEFAULT_STATUS;
    }

    if (!isUserListedInAnyTierPool(groups, dashboardUserAuthId)) {
      return DEFAULT_STATUS;
    }

    const assignedTier = getUserAssignedTier(groups, dashboardUserAuthId);

    // Trial check (supports ms epoch or ISO string)
    const rawTrialEnd = organization?.trial_end_timestamp;
    const trialEndMs =
      typeof rawTrialEnd === 'string' ? Date.parse(rawTrialEnd) : rawTrialEnd;

    const isOnTrial =
      Number.isFinite(trialEndMs) && now !== null && now <= trialEndMs;

    // Mirrors the API's SubscriptionTierService.getEffectiveTier: an active trial elevates
    // the effective tier to Respond regardless of the assigned seat pool.
    const effectiveTier = isOnTrial ? SubscriptionTier.Respond : assignedTier;

    return {
      assignedTier,
      effectiveTier,
      isOnTrial,
      hasAccess: effectiveTier !== SubscriptionTier.Connect,
      hasRespondAccess: effectiveTier === SubscriptionTier.Respond,
    };
  }, [groups, organization?.trial_end_timestamp, dashboardUserAuthId, now]);
};

/**
 * A wrapper that provides a controlled `nowMs` value to the pure hook.
 * - By default it sets `nowMs` once on mount (conservative behavior) to avoid
 *   frequent re-renders. Pass `tickMs` to update periodically.
 */
export const useSubscriptionStatusWithClock = (
  groups,
  organization,
  { tickMs = 30000 } = {},
) => {
  const [now, setNow] = useState(null);
  useEffect(() => {
    // schedule update asynchronously to avoid impure calls during render
    const timer = setTimeout(() => setNow(Date.now()), 0);
    if (!tickMs) return () => clearTimeout(timer);
    const interval = setInterval(() => setNow(Date.now()), tickMs);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [tickMs, organization?.trial_end_timestamp]);

  return useSubscriptionStatus(groups, organization, now);
};
